import { useCallback, useEffect, useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api } from '../../api/http'
import { useSession } from '../../api/session'
import type {
  AdminSecrets, AdminStats, AuditEntry, Invite, SecretState, ServerSettings, SharedProvider, UsageStatus, User
} from '../../api/types'
import { Button } from '../../components/ui/Button'
import { Badge, Card, EmptyState } from '../../components/ui/Card'
import { confirmDialog } from '../../components/ui/Confirm'
import {
  Cell, Count, FilterChips, Pager, Row, RowMenu, SearchBox, Table, Td, Th, Tile, Tiles, Toolbar,
  downloadCsv, useTable, type Rail
} from '../../components/ui/DataTable'
import { Checkbox, Field, Input, Switch } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { formatBytes, formatDateTime, formatUsd } from '../../lib/format'

/**
 * Administration: users, invites, shared providers, server settings, the audit log and system
 * status. Every list is a ledger table (3 px rail, one row per record) and every edit happens in a
 * popup, never inside a row. See docs/web-ui.md §6 and docs/mockups/settings-redesign.html.
 */

/** Loader shared by the admin endpoints. */
function useLoad<T>(path: string) {
  const [data, setData] = useState<T | null>(null)
  const toast = useToast()
  const reload = useCallback(() => api.get<T>(path).then(setData).catch(err => toast((err as Error).message, 'error')), [path, toast])
  useEffect(() => { void reload() }, [reload])
  return { data, reload }
}

/** Panel head every page in this file starts with (mockup `.panel-head`). */
function PanelHead({ eyebrow, title, lede, status, action }: {
  eyebrow: ReactNode; title: ReactNode; lede?: ReactNode; status?: ReactNode; action?: ReactNode
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-6 border-b border-line pb-4">
      <div className="min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-500">{eyebrow}</p>
        <h1 className="mt-2 font-display text-[30px] font-bold tracking-tight">{title}</h1>
        {lede && <p className="mt-2 max-w-[70ch] text-sm text-ink-400">{lede}</p>}
        {status && <p className="mt-2 text-[13.5px] text-ink-400">{status}</p>}
      </div>
      {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
    </header>
  )
}

/** Status line: bold values separated by ` · ` (mockup `.statusline`). */
function StatusLine({ items }: { items: ReactNode[] }) {
  return (
    <>
      {items.map((item, i) => (
        <span key={i}>
          {i > 0 && <span className="text-ink-500"> · </span>}
          {item}
        </span>
      ))}
    </>
  )
}

/** Uppercase section label with a hairline rule (mockup `.divider-label`). */
function SectionLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 pt-1 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-500">
      {label}
      <span className="h-px flex-1 bg-cream-200" />
    </div>
  )
}

/** Every admin page carries this quiet note. */
function AdminNote() {
  const { t } = useTranslation()
  return <span className="text-ink-500">{t('settings.nav.adminOnly')}</span>
}

/* ================================================================ users ==== */

/** `usage` may be absent (older payloads / a failing usage lookup): never crash on it. */
type AdminUser = User & { usage?: UsageStatus }
type UserFilter = 'all' | 'admins' | 'disabled' | 'over'
const USER_FILTERS: UserFilter[] = ['all', 'admins', 'disabled', 'over']

function spent(u: AdminUser): number {
  return u.usage?.sharedCostUsd ?? 0
}

/** No spend yet on a zero budget divides to NaN, which compares false — exactly what we want. */
function isOverBudget(u: AdminUser): boolean {
  return u.budgetUsd !== null && spent(u) / u.budgetUsd >= 0.8
}

function userRail(u: AdminUser): Rail | undefined {
  if (u.status === 'disabled') return 'idle'
  if (isOverBudget(u)) return 'attention'
  return undefined
}

function UserModal({ user, isSelf, onClose, onSaved }: {
  user: AdminUser; isSelf: boolean; onClose(): void; onSaved(): void
}) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [name, setName] = useState(user.name)
  const [role, setRole] = useState<User['role']>(user.role)
  const [status, setStatus] = useState<User['status']>(user.status)
  const [budget, setBudget] = useState(user.budgetUsd === null ? '' : String(user.budgetUsd))
  const [quota, setQuota] = useState(user.storageQuotaMb === null ? '' : String(user.storageQuotaMb))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    const body: { name?: string; role?: User['role']; status?: User['status']; budgetUsd?: number | null; storageQuotaMb?: number | null } = {}
    if (name.trim() && name.trim() !== user.name) body.name = name.trim()
    if (role !== user.role) body.role = role
    if (status !== user.status) body.status = status
    const nextBudget = budget.trim() === '' ? null : Number(budget)
    if (nextBudget !== user.budgetUsd) body.budgetUsd = nextBudget
    const nextQuota = quota.trim() === '' ? null : Number(quota)
    if (nextQuota !== user.storageQuotaMb) body.storageQuotaMb = nextQuota
    if (Object.keys(body).length === 0) { onClose(); return }
    setBusy(true)
    try { await api.patch(`/api/admin/users/${user.id}`, body); toast(t('common.saved'), 'success'); onSaved() }
    catch (err) { toast((err as Error).message, 'error') }
    finally { setBusy(false) }
  }

  const remove = async () => {
    if (!await confirmDialog(t('admin.users.confirmDelete', { email: user.email }))) return
    try { await api.del(`/api/admin/users/${user.id}`); toast(t('common.saved'), 'success'); onSaved() }
    catch (err) { toast((err as Error).message, 'error') }
  }

  return (
    <Modal
      open wide title={user.name} description={user.email} onClose={onClose}
      footer={<>
        {!isSelf && <Button variant="danger" className="sm:mr-auto" onClick={() => void remove()}>{t('common.delete')}</Button>}
        <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button loading={busy} onClick={() => void save()}>{t('common.save')}</Button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('auth.name')}>{id => <Input id={id} value={name} onChange={e => setName(e.target.value)} />}</Field>
          <Field label={t('auth.email')}>{id => <Input id={id} value={user.email} disabled />}</Field>
          <Field label={t('admin.users.role')} hint={isSelf ? t('admin.users.selfRole') : t('admin.users.roleHint')}>{id => (
            <Select
              id={id} value={role} disabled={isSelf} onChange={v => setRole(v)}
              options={[
                { value: 'member', label: t('admin.users.member') },
                { value: 'admin', label: t('admin.users.admin') },
              ]}
            />
          )}</Field>
          <Field label={t('admin.users.status')} hint={isSelf ? t('admin.users.selfStatus') : t('admin.users.statusHint')}>{id => (
            <Select
              id={id} value={status} disabled={isSelf} onChange={v => setStatus(v)}
              options={[
                { value: 'active', label: t('admin.users.active') },
                { value: 'disabled', label: t('admin.users.disabled') },
              ]}
            />
          )}</Field>
        </div>

        <SectionLabel label={t('admin.users.limits')} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.users.budgetPerPeriod')} hint={t('admin.users.budgetDefault')}>{id => (
            <Input id={id} type="number" min={0} step="0.5" placeholder={t('admin.users.defaultBudget')} value={budget} onChange={e => setBudget(e.target.value)} />
          )}</Field>
          <Field label={t('admin.users.quotaMb')}>{id => (
            <Input id={id} type="number" min={1} value={quota} onChange={e => setQuota(e.target.value)} />
          )}</Field>
        </div>

        <SectionLabel label={t('admin.users.thisPeriod')} />
        <Tiles>
          <Tile
            label={t('admin.users.spendPeriod')} value={formatUsd(spent(user))}
            note={user.usage ? t('usage.resets', { date: formatDateTime(user.usage.periodEnd, i18n.language) }) : undefined}
          />
        </Tiles>
      </div>
    </Modal>
  )
}

export function Users() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { user: me } = useSession()
  const { data, reload } = useLoad<{ items: AdminUser[] }>('/api/admin/users')
  const [selected, setSelected] = useState<string | null>(null)
  const items = data?.items ?? []
  const table = useTable(items, {
    search: u => `${u.name} ${u.email}`,
    filter: (u, f) => f === 'admins' ? u.role === 'admin' : f === 'disabled' ? u.status === 'disabled' : f === 'over' ? isOverBudget(u) : true,
    perPage: 25
  })
  const over = items.filter(isOverBudget).length
  const disabled = items.filter(u => u.status === 'disabled').length
  const open = items.find(u => u.id === selected) ?? null

  const setStatus = async (u: AdminUser, status: User['status']) => {
    try { await api.patch(`/api/admin/users/${u.id}`, { status }); void reload() } catch (err) { toast((err as Error).message, 'error') }
  }
  const remove = async (u: AdminUser) => {
    if (!await confirmDialog(t('admin.users.confirmDelete', { email: u.email }))) return
    try { await api.del(`/api/admin/users/${u.id}`); void reload() } catch (err) { toast((err as Error).message, 'error') }
  }

  return (
    <>
      <PanelHead
        eyebrow={`${t('admin.title')} / ${t('settings.nav.users')}`}
        title={t('admin.users.title')}
        lede={<>{t('admin.users.roleHint')} <AdminNote /></>}
        status={<StatusLine items={[
          <>{t('admin.users.title')}: <b>{items.length}</b></>,
          over > 0
            ? <span className="font-semibold text-amber-700">{t('admin.users.overBudget')}: <b>{over}</b></span>
            : <>{t('admin.users.overBudget')}: <b>0</b></>,
          <>{t('admin.users.disabled')}: <b>{disabled}</b></>
        ]} />}
        action={
          <Link to="/settings/invites" className="inline-flex h-10 items-center rounded-[10px] border border-cream-300 bg-cream-50 px-4 text-sm font-medium text-ink-100 hover:bg-cream-200">
            {t('admin.invites.title')}
          </Link>
        }
      />

      <Toolbar>
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('admin.users.searchPlaceholder')} />
        <FilterChips
          value={table.filter as UserFilter} onChange={v => table.setFilter(v)} label={t('admin.users.filterLabel')}
          options={USER_FILTERS.map(f => ({ value: f, label: f === 'all' ? t('common.all') : t(`admin.users.filter_${f}`) }))}
        />
        <Count>{t('admin.users.count', { count: table.total, total: items.length })}</Count>
      </Toolbar>

      {items.length === 0 ? <EmptyState title={t('admin.users.title')} /> : (
        <Table
          footer={<>
            <span>{t('admin.users.budgetDefault')}</span>
            <span className="flex-1" />
            <Pager page={table.page} pages={table.pages} onChange={table.setPage} />
          </>}
        >
          <thead>
            <tr>
              <Th rail />
              <Th>{t('auth.email')}</Th>
              <Th>{t('admin.users.role')}</Th>
              <Th>{t('admin.users.status')}</Th>
              <Th className="text-right">{t('admin.users.spendPeriod')}</Th>
              <Th className="text-right">{t('admin.users.budget')}</Th>
              <Th>{t('admin.users.lastSignIn')}</Th>
              <Th><span className="sr-only">{t('admin.users.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(u => (
              <Row key={u.id} rail={userRail(u)} onClick={() => setSelected(u.id)}>
                <Td><Cell title={u.name} sub={u.email} /></Td>
                <Td><Badge tone={u.role === 'admin' ? 'accent' : 'neutral'}>{t(`admin.users.${u.role}`)}</Badge></Td>
                <Td>{u.status === 'active'
                  ? <Badge tone="success">{t('admin.users.active')}</Badge>
                  : <Badge tone="danger">{t('admin.users.disabled')}</Badge>}</Td>
                <Td className="text-right tabular-nums">{formatUsd(spent(u))}</Td>
                <Td className="text-right tabular-nums">
                  {u.budgetUsd === null ? <span className="text-ink-500">{t('admin.users.defaultBudget')}</span> : formatUsd(u.budgetUsd)}
                </Td>
                <Td className="text-xs text-ink-500">{u.lastLoginAt ? formatDateTime(u.lastLoginAt, i18n.language) : t('admin.users.neverSignedIn')}</Td>
                <Td>
                  <RowMenu items={[
                    { label: t('common.edit'), onSelect: () => setSelected(u.id) },
                    ...(u.id === me?.id ? [] : [{
                      label: u.status === 'active' ? t('admin.users.disable') : t('admin.users.enable'),
                      onSelect: () => void setStatus(u, u.status === 'active' ? 'disabled' : 'active')
                    }]),
                    ...(u.id === me?.id ? [] : [{ label: t('common.delete'), danger: true, divider: true, onSelect: () => void remove(u) }])
                  ]} />
                </Td>
              </Row>
            ))}
          </tbody>
        </Table>
      )}

      {open && (
        <UserModal
          key={open.id} user={open} isSelf={open.id === me?.id}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); void reload() }}
        />
      )}
    </>
  )
}

/* ============================================================== invites ==== */

type InviteStatus = 'open' | 'used' | 'revoked' | 'expired'
type InviteFilter = 'all' | InviteStatus
const INVITE_FILTERS: InviteFilter[] = ['all', 'open', 'used', 'expired', 'revoked']

function inviteStatus(i: Invite): InviteStatus {
  if (i.usedAt) return 'used'
  if (i.revokedAt) return 'revoked'
  if (i.expiresAt < Date.now()) return 'expired'
  return 'open'
}

const INVITE_RAIL: Record<InviteStatus, Rail> = { open: 'ok', used: 'idle', revoked: 'idle', expired: 'error' }
const INVITE_TONE: Record<InviteStatus, 'accent' | 'success' | 'danger' | 'neutral'> = {
  open: 'accent', used: 'success', revoked: 'neutral', expired: 'danger'
}

export function Invites() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { data, reload } = useLoad<{ items: Invite[] }>('/api/admin/invites')
  const items = data?.items ?? []
  const [creating, setCreating] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<User['role']>('member')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState<{ url: string; expiresAt: number } | null>(null)

  const table = useTable(items, {
    search: i => i.email ?? '',
    filter: (i, f) => f === 'all' || inviteStatus(i) === f,
    perPage: 25
  })
  const counts = INVITE_FILTERS.filter((f): f is InviteStatus => f !== 'all')
    .map(f => ({ status: f, count: items.filter(i => inviteStatus(i) === f).length }))

  const create = async () => {
    setBusy(true)
    try {
      const r = await api.post<{ url: string; invite: Invite }>('/api/admin/invites', {
        ...(email.trim() ? { email: email.trim() } : {}), role
      })
      setCreating(false); setEmail(''); setRole('member')
      setLink({ url: r.url, expiresAt: r.invite.expiresAt })
      void reload()
    } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  return (
    <>
      <PanelHead
        eyebrow={`${t('admin.title')} / ${t('settings.nav.invites')}`}
        title={t('admin.invites.title')}
        lede={<>{t('admin.invites.hint')} <AdminNote /></>}
        status={<StatusLine items={counts.map(c => <>{t(`admin.invites.status_${c.status}`)}: <b>{c.count}</b></>)} />}
        action={<Button onClick={() => setCreating(true)}>{t('admin.invites.create')}</Button>}
      />

      <Toolbar>
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('admin.invites.searchPlaceholder')} />
        <FilterChips
          value={table.filter as InviteFilter} onChange={v => table.setFilter(v)} label={t('admin.invites.filterLabel')}
          options={INVITE_FILTERS.map(f => ({ value: f, label: f === 'all' ? t('common.all') : t(`admin.invites.status_${f}`) }))}
        />
        <Count>{t('admin.invites.count', { count: table.total })}</Count>
      </Toolbar>

      {items.length === 0 ? <EmptyState title={t('admin.invites.empty')} /> : (
        <Table
          footer={<>
            <span>{t('admin.invites.hint')}</span>
            <span className="flex-1" />
            <Pager page={table.page} pages={table.pages} onChange={table.setPage} />
          </>}
        >
          <thead>
            <tr>
              <Th rail />
              <Th>{t('auth.email')}</Th>
              <Th>{t('admin.users.role')}</Th>
              <Th>{t('admin.users.status')}</Th>
              <Th>{t('admin.invites.expires', { date: '' }).trim()}</Th>
              <Th>{t('admin.invites.createdBy')}</Th>
              <Th><span className="sr-only">{t('admin.users.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(i => {
              const st = inviteStatus(i)
              return (
                <Row key={i.id} rail={INVITE_RAIL[st]}>
                  <Td>{i.email ? <span className="font-semibold">{i.email}</span> : <span className="text-ink-500">{t('admin.invites.anyEmail')}</span>}</Td>
                  <Td><Badge tone="neutral">{t(`admin.users.${i.role}`)}</Badge></Td>
                  <Td><Badge tone={INVITE_TONE[st]}>{t(`admin.invites.status_${st}`)}</Badge></Td>
                  <Td className="text-xs text-ink-500">{formatDateTime(i.expiresAt, i18n.language)}</Td>
                  <Td><span className="font-mono text-xs text-ink-500">{i.createdBy}</span></Td>
                  <Td>
                    <RowMenu items={st === 'open'
                      ? [{
                          label: t('admin.invites.revoke'), danger: true, onSelect: async () => {
                            try { await api.del(`/api/admin/invites/${i.id}`); void reload() } catch (err) { toast((err as Error).message, 'error') }
                          }
                        }]
                      : []} />
                  </Td>
                </Row>
              )
            })}
          </tbody>
        </Table>
      )}

      <Modal
        open={creating} title={t('admin.invites.inviteTitle')} description={t('admin.invites.inviteHint')}
        onClose={() => setCreating(false)}
        footer={<>
          <Button variant="ghost" onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
          <Button loading={busy} onClick={() => void create()}>{t('admin.invites.create')}</Button>
        </>}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('auth.email')} hint={t('admin.invites.emailOptional')}>{id => (
            <Input id={id} type="email" value={email} onChange={e => setEmail(e.target.value)} />
          )}</Field>
          <Field label={t('admin.users.role')}>{id => (
            <Select
              id={id} value={role} onChange={v => setRole(v)}
              options={[
                { value: 'member', label: t('admin.users.member') },
                { value: 'admin', label: t('admin.users.admin') },
              ]}
            />
          )}</Field>
        </div>
      </Modal>

      <Modal
        open={link !== null} title={t('admin.invites.createdTitle')} description={t('admin.invites.createdHint')}
        onClose={() => setLink(null)}
        footer={<Button onClick={() => setLink(null)}>{t('common.close')}</Button>}
      >
        {link && (
          <div className="space-y-2">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <Input readOnly className="font-mono text-xs" value={link.url} onFocus={e => e.target.select()} aria-label={t('admin.invites.createdTitle')} />
              </div>
              <Button variant="secondary" onClick={() => { void navigator.clipboard?.writeText(link.url); toast(t('common.copied'), 'success') }}>
                {t('admin.invites.copyLink')}
              </Button>
            </div>
            <p className="text-xs text-ink-500">{t('admin.invites.expiresOn', { date: formatDateTime(link.expiresAt, i18n.language) })}</p>
          </div>
        )}
      </Modal>
    </>
  )
}

/* ============================================================ providers ==== */

type PriceMap = Record<string, { input: number; output: number } | undefined>
type ProviderFilter = 'all' | 'on' | 'off' | 'unpriced'
const PROVIDER_FILTERS: ProviderFilter[] = ['all', 'on', 'off', 'unpriced']

export interface ProviderForm {
  id?: string
  name: string
  providerType: SharedProvider['providerType']
  baseUrl: string
  models: string
  apiKey: string
  enabled: boolean
  prices: PriceMap
}

/** Model ids in a comma-separated list, in order and de-duplicated. */
export function modelIds(models: string): string[] {
  return models.split(',').map(s => s.trim()).filter(Boolean).filter((m, i, a) => a.indexOf(m) === i)
}

/** Rows to show: the models listed above, plus any priced model whose id was removed. */
export function priceRows(form: ProviderForm): string[] {
  const ids = modelIds(form.models)
  return [...ids, ...Object.keys(form.prices).filter(m => !ids.includes(m))]
}

/** Remove a model from the provider form: drops it from the models list and deletes its prices. */
export function removeModelFromForm(form: ProviderForm, model: string): ProviderForm {
  const nextModels = modelIds(form.models).filter(id => id !== model).join(', ')
  const prices = { ...form.prices }
  delete prices[model]
  return { ...form, models: nextModels, prices }
}

function unpricedCount(p: SharedProvider): number {
  return p.models.filter(m => !p.prices[m]).length
}

/** First priced model, shown as a quiet "$in / $out" summary. */
function priceSummary(p: SharedProvider): string {
  const first = p.models.find(m => p.prices[m])
  const v = first ? p.prices[first] : undefined
  return v ? `$${v.input} / $${v.output}` : ''
}

function providerRail(p: SharedProvider): Rail {
  if (unpricedCount(p) > 0) return 'attention'
  if (!p.enabled) return 'idle'
  return 'ok'
}

function newProviderForm(): ProviderForm {
  return { name: '', providerType: 'anthropic', baseUrl: '', models: '', apiKey: '', enabled: true, prices: {} }
}

function editProviderForm(p: SharedProvider): ProviderForm {
  const prices: PriceMap = {}
  for (const m of p.models) {
    const v = p.prices[m]
    prices[m] = v ? { input: v.input, output: v.output } : undefined
  }
  return {
    id: p.id, name: p.name, providerType: p.providerType, baseUrl: p.baseUrl ?? '',
    models: p.models.join(', '), apiKey: '', enabled: p.enabled, prices
  }
}

function ProviderModal({ provider, onClose, onSaved }: {
  provider: SharedProvider | null; onClose(): void; onSaved(): void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const [form, setForm] = useState<ProviderForm>(() => (provider ? editProviderForm(provider) : newProviderForm()))
  const [busy, setBusy] = useState(false)
  const [fetchingModels, setFetchingModels] = useState(false)
  const modelIdsId = useId()

  // Keep the price rows in sync with the model list: new ids appear empty, removed ids drop out.
  const changeModels = (models: string) => {
    setForm(f => {
      const prices: PriceMap = {}
      for (const m of modelIds(models)) prices[m] = f.prices[m]
      return { ...f, models, prices }
    })
  }

  const fetchModels = async () => {
    if (!form.apiKey.trim() && !form.id) {
      toast(t('admin.providers.apiKeyRequiredToFetch'), 'error')
      return
    }
    if (form.providerType === 'openai-compatible' && !form.baseUrl.trim()) {
      toast(t('admin.providers.baseUrlRequiredToFetch'), 'error')
      return
    }
    setFetchingModels(true)
    try {
      const res = await api.post<{ models: Array<{ id: string; pricing?: { input: number; output: number } }> }>(
        '/api/admin/providers/fetch-models',
        {
          id: form.id || undefined,
          providerType: form.providerType,
          baseUrl: form.baseUrl.trim() || undefined,
          apiKey: form.apiKey.trim() || undefined,
        }
      )
      if (!res.models || res.models.length === 0) {
        toast(t('admin.providers.noModelsFound'), 'error')
        return
      }
      const existing = modelIds(form.models)
      const newIds = res.models.map(m => m.id)
      const merged = Array.from(new Set([...existing, ...newIds]))
      setForm(f => {
        const prices: PriceMap = {}
        for (const m of merged) {
          if (f.prices[m]) {
            prices[m] = f.prices[m]
          } else {
            const discovered = res.models.find(x => x.id === m)
            if (discovered?.pricing) {
              prices[m] = discovered.pricing
            }
          }
        }
        return {
          ...f,
          models: merged.join(', '),
          prices,
        }
      })
      toast(t('admin.providers.fetchModelsSuccess', { count: res.models.length }), 'success')
    } catch (err) {
      toast((err as Error).message, 'error')
    } finally {
      setFetchingModels(false)
    }
  }

  const setPrice = (model: string, key: 'input' | 'output', raw: string) => {
    setForm(f => {
      const cur = f.prices[model] ?? { input: 0, output: 0 }
      const next = { ...cur, [key]: raw === '' ? 0 : Number(raw) }
      const prices = { ...f.prices }
      if (next.input === 0 && next.output === 0) delete prices[model]
      else prices[model] = next
      return { ...f, prices }
    })
  }

  const removeModel = (model: string) => setForm(f => removeModelFromForm(f, model))

  const save = async () => {
    if (!form.name.trim()) {
      toast(t('admin.providers.nameRequired'), 'error')
      return
    }
    const ids = modelIds(form.models)
    if (ids.length === 0) {
      toast(t('admin.providers.modelsRequired'), 'error')
      return
    }
    const prices: Record<string, { input: number; output: number }> = {}
    for (const m of ids) {
      const v = form.prices[m]
      if (v) prices[m] = v
    }
    const body = {
      name: form.name.trim(), providerType: form.providerType, baseUrl: form.baseUrl.trim() || null,
      models: ids, prices, enabled: form.enabled,
      ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {})
    }
    setBusy(true)
    try {
      if (form.id) await api.patch(`/api/admin/providers/${form.id}`, body)
      else await api.post('/api/admin/providers', body)
      toast(t('common.saved'), 'success')
      onSaved()
    } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  const remove = async () => {
    if (!form.id || !await confirmDialog(t('admin.providers.confirmDelete', { name: form.name }))) return
    try { await api.del(`/api/admin/providers/${form.id}`); toast(t('common.saved'), 'success'); onSaved() }
    catch (err) { toast((err as Error).message, 'error') }
  }

  const rows = priceRows(form)
  return (
    <Modal
      open wide title={form.id ? t('admin.providers.edit') : t('admin.providers.add')}
      description={form.id ? t('admin.providers.keepKey') : t('admin.providers.hint')} onClose={onClose}
      footer={<>
        {form.id && <Button variant="danger" className="sm:mr-auto" onClick={() => void remove()}>{t('common.delete')}</Button>}
        <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button loading={busy} onClick={() => void save()}>{t('common.save')}</Button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.providers.name')}>{id => <Input id={id} required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />}</Field>
          <Field label={t('settings.providerType')}>{id => (
            <Select
              id={id} value={form.providerType}
              onChange={v => setForm({ ...form, providerType: v })}
              options={[
                { value: 'anthropic', label: 'Anthropic' },
                { value: 'google', label: 'Google Gemini' },
                { value: 'openai-compatible', label: 'OpenAI-compatible' },
              ]}
            />
          )}</Field>
          <Field label={t('settings.apiKey')} hint={form.id ? t('admin.providers.keepKey') : undefined}>{id => (
            <Input id={id} type="password" autoComplete="off" value={form.apiKey} onChange={e => setForm({ ...form, apiKey: e.target.value })} />
          )}</Field>
          <Field label={t('settings.baseUrl')} hint={t('admin.providers.baseUrlHint')}>{id => (
            <Input id={id} type="url" value={form.baseUrl} onChange={e => setForm({ ...form, baseUrl: e.target.value })} />
          )}</Field>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor={modelIdsId} className="text-xs font-semibold text-ink-300">{t('settings.modelIds')}</label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={fetchingModels}
              onClick={() => void fetchModels()}
            >
              {t('admin.providers.fetchModels')}
            </Button>
          </div>
          <Input id={modelIdsId} required value={form.models} onChange={e => changeModels(e.target.value)} placeholder="deepseek-chat, deepseek-reasoner" />
          <p className="text-xs text-ink-500">{t('admin.providers.modelIdsHint')}</p>
        </div>

        <SectionLabel label={t('admin.providers.pricesTitle')} />
        {rows.length === 0 ? <p className="text-sm text-ink-500">{t('admin.providers.pricesEmpty')}</p> : (
          <Table minWidth={420}>
            <thead>
              <tr>
                <Th rail />
                <Th>{t('admin.providers.colModels')}</Th>
                <Th className="text-right">{t('admin.providers.priceIn')}</Th>
                <Th className="text-right">{t('admin.providers.priceOut')}</Th>
                <Th><span className="sr-only">{t('admin.providers.removeModel')}</span></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(m => {
                const v = form.prices[m]
                return (
                  <Row key={m} rail={v ? undefined : 'attention'}>
                    <Td><span className="font-mono text-xs">{m}</span></Td>
                    <Td className="text-right">
                      <div className="ml-auto w-24">
                        <Input
                          type="number" min={0} step="0.01" className="h-8 py-1 text-right text-xs"
                          value={v ? v.input : ''} placeholder="—" aria-label={t('admin.providers.priceIn')}
                          onChange={e => setPrice(m, 'input', e.target.value)}
                        />
                      </div>
                    </Td>
                    <Td className="text-right">
                      <div className="ml-auto w-24">
                        <Input
                          type="number" min={0} step="0.01" className="h-8 py-1 text-right text-xs"
                          value={v ? v.output : ''} placeholder="—" aria-label={t('admin.providers.priceOut')}
                          onChange={e => setPrice(m, 'output', e.target.value)}
                        />
                      </div>
                    </Td>
                    <Td className="text-right">
                      <Button variant="ghost" size="icon-sm" aria-label={t('admin.providers.removeModel')} onClick={() => removeModel(m)}>✕</Button>
                    </Td>
                  </Row>
                )
              })}
            </tbody>
          </Table>
        )}
        <p className="text-xs text-ink-500">{t('admin.providers.pricesHint')}</p>
        <Checkbox label={t('admin.providers.enableForAll')} checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
      </div>
    </Modal>
  )
}

export function Providers() {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, reload } = useLoad<{ items: SharedProvider[] }>('/api/admin/providers')
  const items = data?.items ?? []
  const [editing, setEditing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const table = useTable(items, {
    search: p => `${p.id} ${p.name} ${p.models.join(' ')}`,
    filter: (p, f) => f === 'on' ? p.enabled : f === 'off' ? !p.enabled : f === 'unpriced' ? unpricedCount(p) > 0 : true,
    perPage: 25
  })
  const enabled = items.filter(p => p.enabled).length
  const models = items.reduce((n, p) => n + p.models.length, 0)
  const missing = items.reduce((n, p) => n + unpricedCount(p), 0)
  const open = items.find(p => p.id === editing) ?? null

  return (
    <>
      <PanelHead
        eyebrow={`${t('admin.title')} / ${t('settings.nav.providers')}`}
        title={t('admin.providers.title')}
        lede={<>{t('admin.providers.hint')} <AdminNote /></>}
        status={<StatusLine items={[
          <>{t('admin.providers.filter_on')}: <b>{enabled}</b></>,
          <>{t('admin.providers.colModels')}: <b>{models}</b></>,
          missing > 0
            ? <span className="font-semibold text-amber-700">{t('admin.providers.missingPrices', { count: missing })}</span>
            : <>{t('admin.providers.missingPrices', { count: 0 })}</>
        ]} />}
        action={<Button onClick={() => setCreating(true)}>{t('admin.providers.add')}</Button>}
      />

      <Toolbar>
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('admin.providers.searchPlaceholder')} />
        <FilterChips
          value={table.filter as ProviderFilter} onChange={v => table.setFilter(v)} label={t('admin.providers.filterLabel')}
          options={PROVIDER_FILTERS.map(f => ({ value: f, label: f === 'all' ? t('common.all') : t(`admin.providers.filter_${f}`) }))}
        />
        <Count>{t('admin.providers.count', { count: table.total })}</Count>
      </Toolbar>

      {items.length === 0 ? <EmptyState title={t('admin.providers.empty')} body={t('admin.providers.emptyBody')} /> : (
        <Table
          footer={<>
            <span>{t('admin.providers.pricesHint')}</span>
            <span className="flex-1" />
            <Pager page={table.page} pages={table.pages} onChange={table.setPage} />
          </>}
        >
          <thead>
            <tr>
              <Th rail />
              <Th>{t('admin.providers.title')}</Th>
              <Th>{t('settings.providerType')}</Th>
              <Th className="text-right">{t('admin.providers.colModels')}</Th>
              <Th>{t('admin.providers.colPrices')}</Th>
              <Th>{t('admin.users.status')}</Th>
              <Th><span className="sr-only">{t('admin.users.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(p => {
              const missingHere = unpricedCount(p)
              return (
                <Row key={p.id} rail={providerRail(p)} onClick={() => setEditing(p.id)}>
                  <Td><Cell title={p.name} sub={<span className="font-mono">{p.id}{p.hasKey ? ` · ${t('admin.providers.keyHint')}` : ''}</span>} /></Td>
                  <Td><Badge tone="neutral">{p.providerType}</Badge></Td>
                  <Td className="text-right tabular-nums">{p.models.length}</Td>
                  <Td className="text-right">
                    {missingHere > 0
                      ? <span className="font-mono text-xs text-amber-700">{t('admin.providers.missingPrices', { count: missingHere })}</span>
                      : <span className="font-mono text-xs">{priceSummary(p)}</span>}
                  </Td>
                  <Td>{p.enabled ? <Badge tone="success">{t('admin.providers.enabled')}</Badge> : <Badge tone="neutral">{t('admin.providers.disabled')}</Badge>}</Td>
                  <Td>
                    <RowMenu items={[
                      { label: t('common.edit'), onSelect: () => setEditing(p.id) },
                      {
                        label: t('common.delete'), danger: true, divider: true, onSelect: async () => {
                          if (!await confirmDialog(t('admin.providers.confirmDelete', { name: p.name }))) return
                          try { await api.del(`/api/admin/providers/${p.id}`); void reload() } catch (err) { toast((err as Error).message, 'error') }
                        }
                      }
                    ]} />
                  </Td>
                </Row>
              )
            })}
          </tbody>
        </Table>
      )}

      {(creating || open) && (
        <ProviderModal
          key={open?.id ?? 'new'} provider={open}
          onClose={() => { setCreating(false); setEditing(null) }}
          onSaved={() => { setCreating(false); setEditing(null); void reload() }}
        />
      )}
    </>
  )
}

/* ======================================================= server settings ==== */

const SETTINGS_KEYS: (keyof ServerSettings)[] = [
  'budget', 'storageQuotaMb', 'approvalTimeoutHours', 'cronMinIntervalMinutes',
  'defaultModel', 'registration', 'sandboxAccess', 'retention', 'kbEmbedding',
  'socialUrl', 'zimbraUrl', 'zimbraSmtpHost', 'zimbraSmtpPort', 'zimbraImapHost', 'zimbraImapPort',
  'zimbraMailTls', 'jiraHost', 'jiraStrictSsl', 'figmaApiBase', 'githubApiBase', 'telegramApiBase', 'extensionIds',
  'googleClientId', 'oidcIssuer', 'oidcClientId', 'oidcLabel', 'oidcAllowedDomains', 'oidcScopes'
]

/* ============================================================== secrets ==== */

/**
 * Operator secrets (docs/web-ui.md §6): the values an admin used to have to edit into `.env` on the
 * server. The API reports only `configured` / `source` / `hint` — a value is written once through
 * `PATCH /api/admin/secrets` and never read back. The approved mockup (variant A) keeps each secret
 * as a password field inside the card it belongs to, saved by the page's one **Save**.
 */

/** The `.env` variable a secret falls back to, shown in the source box. */
const SECRET_ENV: Record<keyof AdminSecrets, string> = {
  tavilyApiKey: 'TAVILY_API_KEY',
  googleClientSecret: 'GOOGLE_CLIENT_SECRET',
  oidcClientSecret: 'OIDC_CLIENT_SECRET'
}

/** Shortest value the secrets API accepts; a shorter one would be rejected with a 400. */
const SECRET_MIN_LENGTH = 8

/** Where a secret's effective value comes from (mockup: Từ cài đặt / Từ .env / Chưa cấu hình). */
const SECRET_SOURCE_KEY = { settings: 'secretFromSettings', env: 'secretFromEnv' } as const

function SecretSourceBadge({ state }: { state: SecretState }) {
  const { t } = useTranslation()
  return state.source === null
    ? <Badge tone="warning">{t('admin.settings.secretMissing')}</Badge>
    : <Badge tone={state.source === 'settings' ? 'success' : 'neutral'}>{t(`admin.settings.${SECRET_SOURCE_KEY[state.source]}`)}</Badge>
}

/**
 * The "which value wins" box the mockup puts next to every secret field: the vault value, the env
 * variable it overrides, and what clearing the field does (falls back, never disables).
 */
function SecretSourceBox({ name, state, clearHint }: { name: keyof AdminSecrets; state: SecretState; clearHint: string }) {
  const { t } = useTranslation()
  const fromSettings = state.source === 'settings'
  return (
    <div className="rounded-[10px] border border-line px-4 py-3 text-[13.5px]">
      <p className="text-xs font-semibold text-ink-300">{t('admin.settings.secretSource')}</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span>{t('admin.settings.secretSourceVault')}</span>
        {fromSettings
          ? <Badge tone="success">{t('admin.settings.secretActive')}</Badge>
          : <span className="text-xs text-ink-500">{t('admin.settings.secretNotUsed')}</span>}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs text-ink-400">{SECRET_ENV[name]}</span>
        <span className="font-mono text-xs text-ink-400">
          {fromSettings ? t('admin.settings.secretOverridden') : state.hint ?? t('admin.settings.secretEmpty')}
        </span>
      </div>
      <p className="mt-2 text-xs text-ink-500">{clearHint}</p>
    </div>
  )
}

/**
 * One operator secret: a write-only password field with its source badge, next to the "which value
 * wins" box. Shared by the Web search card (Tavily) and the Sign-in card (Google, SSO) — the
 * approved mockup keeps each secret inside the card it belongs to, saved by the page's one **Save**.
 */
function SecretField({ name, label, hint, error, clearHint, state, value, busy, onChange, onClear }: {
  name: keyof AdminSecrets
  label: string
  hint?: string
  error?: string
  /** What clearing the field does, in this card's own words. */
  clearHint: string
  state: SecretState
  value: string
  busy: boolean
  onChange(value: string): void
  onClear(): void
}) {
  const { t } = useTranslation()
  return (
    <>
      <Field label={label} hint={hint} error={error} action={<SecretSourceBadge state={state} />}>{id => (
        <div className="flex items-center gap-2">
          <Input
            id={id} type="password" autoComplete="off" spellCheck={false}
            value={value} placeholder={state.hint ?? t('admin.settings.secretEmpty')}
            onChange={e => onChange(e.target.value)}
          />
          <div className="w-20 shrink-0">
            <Button type="button" variant="ghost" disabled={busy || !state.source} onClick={onClear}>{t('common.delete')}</Button>
          </div>
        </div>
      )}</Field>
      <SecretSourceBox name={name} state={state} clearHint={clearHint} />
    </>
  )
}

/**
 * Service URLs an operator used to have to set in `.env` and restart for (docs/web-ui.md §6). An
 * empty field means "use `.env`" — the placeholder says so — and the change applies to the next
 * call, request or handshake. Rendered inside `ServerSettingsCard`'s form, so the page's one **Save**
 * button and the dirty counter (`SETTINGS_KEYS`) cover it.
 */
function ServiceUrlsCard({ draft, setDraft }: { draft: ServerSettings; setDraft(v: ServerSettings): void }) {
  const { t } = useTranslation()
  const fromEnv = t('admin.settings.urlFromEnv')
  // The placeholder is the state signal: an empty field is "use `.env`", not "unset feature".
  const text = (key: keyof ServerSettings) => ({
    value: (draft[key] as string | null) ?? '',
    placeholder: fromEnv,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, [key]: e.target.value === '' ? null : e.target.value })
  })
  const num = (key: keyof ServerSettings) => ({
    value: (draft[key] as number | null) ?? '',
    placeholder: fromEnv,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, [key]: e.target.value === '' ? null : Number(e.target.value) })
  })
  return (
    <Card title={t('admin.settings.groupUrls')} subtitle={t('admin.settings.groupUrlsCount')}>
      <p className="mb-4 text-sm text-ink-400">{t('admin.settings.urlsHint')}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.settings.socialUrl')} hint={t('admin.settings.socialUrlHint')}>{id => <Input id={id} type="url" {...text('socialUrl')} />}</Field>
        <Field label={t('admin.settings.jiraHost')} hint={t('admin.settings.jiraHostHint')}>{id => <Input id={id} {...text('jiraHost')} />}</Field>
        <Field label={t('admin.settings.figmaApiBase')}>{id => <Input id={id} {...text('figmaApiBase')} />}</Field>
        <Field label={t('admin.settings.githubApiBase')}>{id => <Input id={id} {...text('githubApiBase')} />}</Field>
        <Field label={t('admin.settings.telegramApiBase')}>{id => <Input id={id} {...text('telegramApiBase')} />}</Field>
        <Field label={t('admin.settings.jiraStrictSsl')} hint={t('admin.settings.jiraStrictSslHint')}>{() => (
          <div className="flex h-10 items-center">
            <Switch
              label={t('admin.settings.jiraStrictSsl')}
              checked={draft.jiraStrictSsl ?? true}
              onChange={v => setDraft({ ...draft, jiraStrictSsl: v })}
            />
          </div>
        )}</Field>
      </div>

      <div className="mt-5 border-t border-cream-200 pt-5">
        <p className="mb-3 text-sm font-semibold text-ink-300">{t('admin.settings.zimbra')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.settings.zimbraUrl')} hint={t('admin.settings.zimbraUrlHint')}>{id => <Input id={id} {...text('zimbraUrl')} />}</Field>
          <Field label={t('admin.settings.zimbraMailTls')}>{id => (
            <Select
              id={id} value={draft.zimbraMailTls ?? ''}
              onChange={v => setDraft({ ...draft, zimbraMailTls: v === '' ? null : v as ServerSettings['zimbraMailTls'] })}
              options={[
                { value: '', label: fromEnv },
                { value: 'verify', label: 'verify' },
                { value: 'no-verify', label: 'no-verify' },
                { value: 'off', label: 'off' }
              ]}
            />
          )}</Field>
          <Field label={t('admin.settings.zimbraSmtpHost')}>{id => <Input id={id} {...text('zimbraSmtpHost')} />}</Field>
          <Field label={t('admin.settings.zimbraSmtpPort')}>{id => <Input id={id} type="number" min={1} max={65535} {...num('zimbraSmtpPort')} />}</Field>
          <Field label={t('admin.settings.zimbraImapHost')}>{id => <Input id={id} {...text('zimbraImapHost')} />}</Field>
          <Field label={t('admin.settings.zimbraImapPort')}>{id => <Input id={id} type="number" min={1} max={65535} {...num('zimbraImapPort')} />}</Field>
        </div>
      </div>

      <div className="mt-5 border-t border-cream-200 pt-5">
        <Field label={t('admin.settings.extensionIds')} hint={t('admin.settings.extensionIdsHint')}>{id => (
          <Input
            id={id} className="font-mono" placeholder={fromEnv}
            value={(draft.extensionIds ?? []).join(', ')}
            onChange={e => setDraft({ ...draft, extensionIds: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
          />
        )}</Field>
      </div>
    </Card>
  )
}

/**
 * The Google connector's OAuth client and the single sign-on provider (docs/web-ui.md §6). Both are
 * optional: an empty field means "use `.env`", and the client secrets are write-only fields in this
 * card. **Kiểm tra** runs OIDC discovery + JWKS against what is *saved*, so a typo is caught before
 * the admin locks themselves out; while nothing is configured the login page simply has no SSO button.
 */
function SignInCard({ draft, setDraft, secrets, secretDraft, setSecretDraft, busy, errors, clearSecret }: {
  draft: ServerSettings
  setDraft(v: ServerSettings): void
  secrets: AdminSecrets
  secretDraft: Partial<Record<keyof AdminSecrets, string>>
  setSecretDraft(fn: (d: Partial<Record<keyof AdminSecrets, string>>) => Partial<Record<keyof AdminSecrets, string>>): void
  busy: boolean
  errors: Partial<Record<keyof AdminSecrets, string>>
  clearSecret(name: keyof AdminSecrets): void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const [testing, setTesting] = useState(false)
  const fromEnv = t('admin.settings.urlFromEnv')
  const text = (key: keyof ServerSettings) => ({
    value: (draft[key] as string | null) ?? '',
    placeholder: fromEnv,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, [key]: e.target.value === '' ? null : e.target.value })
  })
  const test = async () => {
    setTesting(true)
    try {
      const r = await api.post<{ ok: true; issuer: string }>('/api/admin/sign-in/test', {})
      toast(t('admin.settings.signInTestOk', { issuer: r.issuer }), 'success')
    } catch (err) { toast((err as Error).message, 'error') } finally { setTesting(false) }
  }
  return (
    <Card
      title={t('admin.settings.groupSignIn')} subtitle={t('admin.settings.groupSignInCount')}
      actions={<Button type="button" variant="secondary" loading={testing} onClick={() => void test()}>{t('admin.settings.signInTest')}</Button>}
    >
      <p className="mb-4 text-sm text-ink-400">{t('admin.settings.signInHint')}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.settings.googleClientId')} hint={t('admin.settings.googleClientIdHint')}>{id => <Input id={id} {...text('googleClientId')} />}</Field>
        <SecretField
          name="googleClientSecret" label={t('admin.settings.googleClientSecret')} hint={t('admin.settings.googleClientSecretHint')}
          error={errors.googleClientSecret} clearHint={t('admin.settings.secretClearHintSignIn')} state={secrets.googleClientSecret}
          value={secretDraft.googleClientSecret ?? ''} busy={busy}
          onChange={v => setSecretDraft(d => ({ ...d, googleClientSecret: v }))}
          onClear={() => clearSecret('googleClientSecret')}
        />

        <Field label={t('admin.settings.oidcIssuer')} hint={t('admin.settings.oidcIssuerHint')}>{id => <Input id={id} {...text('oidcIssuer')} />}</Field>
        <Field label={t('admin.settings.oidcClientId')}>{id => <Input id={id} {...text('oidcClientId')} />}</Field>
        <SecretField
          name="oidcClientSecret" label={t('admin.settings.oidcClientSecret')} hint={t('admin.settings.oidcClientSecretHint')}
          error={errors.oidcClientSecret} clearHint={t('admin.settings.secretClearHintSignIn')} state={secrets.oidcClientSecret}
          value={secretDraft.oidcClientSecret ?? ''} busy={busy}
          onChange={v => setSecretDraft(d => ({ ...d, oidcClientSecret: v }))}
          onClear={() => clearSecret('oidcClientSecret')}
        />
        <Field label={t('admin.settings.oidcLabel')} hint={t('admin.settings.oidcLabelHint')}>{id => <Input id={id} {...text('oidcLabel')} />}</Field>
        <Field label={t('admin.settings.oidcDomains')} hint={t('admin.settings.oidcDomainsHint')}>{id => (
          <Input
            id={id} placeholder={fromEnv}
            value={(draft.oidcAllowedDomains ?? []).join(', ')}
            onChange={e => {
              const domains = e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              setDraft({ ...draft, oidcAllowedDomains: domains.length ? domains : null })
            }}
          />
        )}</Field>
        <Field label={t('admin.settings.oidcScopes')} hint={t('admin.settings.oidcScopesHint')}>{id => <Input id={id} {...text('oidcScopes')} />}</Field>
      </div>
      <p className="mt-4 text-xs text-ink-500">{t('admin.settings.secretsLegend')}</p>
    </Card>
  )
}

export function ServerSettingsCard() {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, reload } = useLoad<ServerSettings>('/api/admin/settings')
  const secrets = useLoad<AdminSecrets>('/api/admin/secrets')
  const providers = useLoad<{ items: SharedProvider[] }>('/api/admin/providers').data
  const [draft, setDraft] = useState<ServerSettings | null>(null)
  // New secret values typed in this session; keyed by secret name. Empty = leave the stored value alone.
  const [secretDraft, setSecretDraft] = useState<Partial<Record<keyof AdminSecrets, string>>>({})
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (data) setDraft(data) }, [data])
  if (!draft || !data) return null
  const loaded = data
  // The settings form still renders when the secrets call fails; only the Web search card needs it.
  const secretState = secrets.data

  const b = draft.budget
  const setBudget = (patch: Partial<ServerSettings['budget']>) => setDraft({ ...draft, budget: { ...b, ...patch } })
  const options = providers?.items.flatMap(p => p.models.map(m => `${p.id}/${m}`)) ?? []
  const current = draft.defaultModel ? `${draft.defaultModel.provider}/${draft.defaultModel.model}` : ''
  const secretPatch = Object.entries(secretDraft).filter(([, v]) => v) as [keyof AdminSecrets, string][]
  const changed = SETTINGS_KEYS.filter(k => JSON.stringify(draft[k]) !== JSON.stringify(loaded[k])).length + secretPatch.length
  const tavilyTyped = secretDraft.tavilyApiKey ?? ''
  // A typed secret shorter than the API's minimum would be rejected with a 400, so block Save.
  const secretErrors = Object.fromEntries((Object.keys(SECRET_ENV) as (keyof AdminSecrets)[]).map(name => {
    const typed = secretDraft[name] ?? ''
    return [name, typed && typed.length < SECRET_MIN_LENGTH
      ? t('admin.settings.secretTooShort', { min: SECRET_MIN_LENGTH })
      : undefined]
  })) as Partial<Record<keyof AdminSecrets, string>>
  const hasSecretError = Object.values(secretErrors).some(Boolean)

  /** Drop unsaved edits, secrets included. */
  const reset = () => { setDraft(loaded); setSecretDraft({}) }

  const save = async () => {
    setBusy(true)
    try {
      await api.patch('/api/admin/settings', draft)
      if (secretPatch.length > 0) {
        await api.patch('/api/admin/secrets', Object.fromEntries(secretPatch))
        setSecretDraft({})
        void secrets.reload()
      }
      toast(t('common.saved'), 'success')
      void reload()
    }
    catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  /** Clear one secret back to the `.env` default (never disables the feature). */
  const clearSecret = async (key: keyof AdminSecrets) => {
    setBusy(true)
    try {
      await api.patch('/api/admin/secrets', { [key]: null })
      setSecretDraft(d => ({ ...d, [key]: '' }))
      toast(t('common.saved'), 'success')
      void secrets.reload()
    } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  return (
    <>
      <PanelHead
        eyebrow={`${t('admin.title')} / ${t('settings.nav.server')}`}
        title={t('admin.settings.title')}
        lede={<>{t('admin.settings.hint')} <AdminNote /></>}
        status={<StatusLine items={[
          <>{t('admin.settings.period')}: <b>{t(`admin.settings.${b.period}`)}</b></>,
          <>{t('admin.settings.timezone')}: <b>{b.timezone}</b></>
        ]} />}
        action={<>
          <Button variant="ghost" onClick={reset}>{t('common.dismiss')}</Button>
          <Button loading={busy} disabled={hasSecretError} onClick={() => void save()}>{t('common.save')}</Button>
        </>}
      />

      <form className="space-y-5" onSubmit={e => { e.preventDefault(); void save() }}>
        <Card title={t('admin.settings.groupBudget')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.settings.period')}>{id => (
              <Select
                id={id} value={b.period}
                onChange={v => setBudget({ period: v, resetDay: 1 })}
                options={[
                  { value: 'monthly', label: t('admin.settings.monthly') },
                  { value: 'weekly', label: t('admin.settings.weekly') },
                  { value: 'daily', label: t('admin.settings.daily') },
                ]}
              />
            )}</Field>
            {b.period !== 'daily' && (
              <Field label={t('admin.settings.resetDay')} hint={b.period === 'weekly' ? t('admin.settings.weekdayHint') : t('admin.settings.monthDayHint')}>{id => (
                <Input id={id} type="number" min={1} max={b.period === 'weekly' ? 7 : 28} value={b.resetDay} onChange={e => setBudget({ resetDay: Number(e.target.value) })} />
              )}</Field>
            )}
            <Field label={t('admin.settings.timezone')} hint={t('admin.settings.resetDayHint')}>{id => (
              <Input id={id} value={b.timezone} onChange={e => setBudget({ timezone: e.target.value })} />
            )}</Field>
            <Field label={t('admin.settings.defaultBudget')} hint={t('admin.settings.defaultBudgetHint')}>{id => (
              <Input id={id} type="number" min={0} step="0.5" value={b.defaultUsd ?? ''} onChange={e => setBudget({ defaultUsd: e.target.value === '' ? null : Number(e.target.value) })} />
            )}</Field>
          </div>
        </Card>

        <Card title={t('admin.settings.groupLimits')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.settings.quota')}>{id => (
              <Input id={id} type="number" min={1} value={draft.storageQuotaMb} onChange={e => setDraft({ ...draft, storageQuotaMb: Number(e.target.value) })} />
            )}</Field>
            <Field label={t('admin.settings.defaultModel')}>{id => (
              <Select
                id={id} value={current}
                onChange={v => {
                  const i = v.indexOf('/')
                  setDraft({ ...draft, defaultModel: v ? { provider: v.slice(0, i), model: v.slice(i + 1) } : null })
                }}
                options={[{ value: '', label: t('admin.settings.firstAvailable') }, ...options.map(o => ({ value: o, label: o }))]}
              />
            )}</Field>
            <Field label={t('admin.settings.approvalTimeout')}>{id => (
              <Input id={id} type="number" min={1} value={draft.approvalTimeoutHours} onChange={e => setDraft({ ...draft, approvalTimeoutHours: Number(e.target.value) })} />
            )}</Field>
            <Field label={t('admin.settings.cronMin')}>{id => (
              <Input id={id} type="number" min={1} max={1440} value={draft.cronMinIntervalMinutes} onChange={e => setDraft({ ...draft, cronMinIntervalMinutes: Number(e.target.value) })} />
            )}</Field>
            <Field label={t('admin.settings.registration')}>{id => (
              <Select
                id={id} value={draft.registration}
                onChange={v => setDraft({ ...draft, registration: v })}
                options={[
                  { value: 'invite', label: t('admin.settings.registrationInvite') },
                  { value: 'open', label: t('admin.settings.registrationOpen') },
                ]}
              />
            )}</Field>
            <Field label={t('admin.settings.sandboxAccess')} hint={t('admin.settings.sandboxHint')}>{id => (
              <Select
                id={id} value={draft.sandboxAccess}
                onChange={v => setDraft({ ...draft, sandboxAccess: v })}
                options={[
                  { value: 'off', label: t('admin.settings.sandboxOff') },
                  { value: 'admins', label: t('admin.settings.sandboxAdmins') },
                  { value: 'all', label: t('admin.settings.sandboxAll') },
                ]}
              />
            )}</Field>
          </div>
        </Card>

        <Card title={t('admin.settings.groupRetention')}>
          <fieldset className="grid gap-4 sm:grid-cols-4">
            <legend className="mb-2 text-sm font-medium text-ink-300">{t('admin.settings.retention')}</legend>
            {([['routineRunsDays', 'retentionRuns'], ['notificationsDays', 'retentionNotifications'], ['auditDays', 'retentionAudit'], ['telegramDays', 'retentionTelegram']] as const).map(([key, label]) => (
              <Field key={key} label={t(`admin.settings.${label}`)}>{id => (
                <Input
                  id={id} type="number" min={key === 'auditDays' ? 30 : 1} value={draft.retention[key]}
                  onChange={e => setDraft({ ...draft, retention: { ...draft.retention, [key]: Number(e.target.value) } })}
                />
              )}</Field>
            ))}
          </fieldset>
        </Card>

        <Card title={t('admin.kbEmbedding.title')}>
          <p className="mb-4 text-sm text-ink-400">{t('admin.kbEmbedding.hint')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.kbEmbedding.provider')}>{id => (
              <Select id={id} value={draft.kbEmbedding.providerId ?? ''}
                onChange={v => setDraft({ ...draft, kbEmbedding: { ...draft.kbEmbedding, providerId: v || null } })}
                options={[{ value: '', label: t('admin.kbEmbedding.none') },
                  ...(providers?.items ?? []).filter(p => p.providerType !== 'anthropic').map(p => ({ value: p.id, label: `${p.name} (${p.providerType})` }))]} />
            )}</Field>
            <Field label={t('admin.kbEmbedding.model')}>{id => (
              <Input id={id} value={draft.kbEmbedding.model} onChange={e => setDraft({ ...draft, kbEmbedding: { ...draft.kbEmbedding, model: e.target.value } })} />
            )}</Field>
            <Field label={t('admin.kbEmbedding.dimensions')} hint={t('admin.kbEmbedding.reembedHint')}>{id => (
              <Input id={id} type="number" min={64} max={4096} value={draft.kbEmbedding.dimensions} onChange={e => setDraft({ ...draft, kbEmbedding: { ...draft.kbEmbedding, dimensions: Number(e.target.value) } })} />
            )}</Field>
            <Field label={t('admin.kbEmbedding.batchSize')}>{id => (
              <Input id={id} type="number" min={1} max={256} value={draft.kbEmbedding.batchSize} onChange={e => setDraft({ ...draft, kbEmbedding: { ...draft.kbEmbedding, batchSize: Number(e.target.value) } })} />
            )}</Field>
            <Field label={t('admin.kbEmbedding.price')}>{id => (
              <Input id={id} type="number" min={0} step="0.01" value={draft.kbEmbedding.pricePerMTokens} onChange={e => setDraft({ ...draft, kbEmbedding: { ...draft.kbEmbedding, pricePerMTokens: Number(e.target.value) } })} />
            )}</Field>
            <div className="flex items-end">
              <Button type="button" variant="secondary" disabled={!draft.kbEmbedding.providerId} onClick={async () => {
                try {
                  const r = await api.post<{ dimensions: number; ms: number }>('/api/admin/kb/embedding/test', draft.kbEmbedding)
                  toast(t('admin.kbEmbedding.testOk', r), 'success')
                } catch (err) { toast((err as Error).message, 'error') }
              }}>{t('admin.kbEmbedding.test')}</Button>
            </div>
          </div>
        </Card>

        {secretState && (
          <Card title={t('admin.settings.groupWebsearch')} subtitle={t('admin.settings.groupWebsearchCount')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <SecretField
                name="tavilyApiKey" label={t('admin.settings.secretTavily')} hint={t('admin.settings.secretTavilyHint')}
                error={secretErrors.tavilyApiKey} clearHint={t('admin.settings.secretClearHint')} state={secretState.tavilyApiKey}
                value={tavilyTyped} busy={busy}
                onChange={v => setSecretDraft(d => ({ ...d, tavilyApiKey: v }))}
                onClear={() => void clearSecret('tavilyApiKey')}
              />
            </div>
            <p className="mt-4 text-xs text-ink-500">{t('admin.settings.secretsLegend')}</p>
          </Card>
        )}

        <ServiceUrlsCard draft={draft} setDraft={setDraft} />

        {secretState && (
          <SignInCard
            draft={draft} setDraft={setDraft} secrets={secretState} secretDraft={secretDraft} setSecretDraft={setSecretDraft}
            busy={busy} errors={secretErrors} clearSecret={name => void clearSecret(name)}
          />
        )}

        {changed > 0 && (
          <div className="sticky bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] flex items-center gap-3 rounded-2xl border border-line bg-cream-50 px-4 py-3 shadow-pop">
            <span>{t('common.unsaved', { count: changed })}</span>
            <span className="flex-1" />
            <Button variant="ghost" onClick={reset}>{t('common.dismiss')}</Button>
            <Button loading={busy} disabled={hasSecretError} onClick={() => void save()}>{t('common.save')}</Button>
          </div>
        )}
      </form>
    </>
  )
}

/* ================================================================ audit ==== */

type AuditFilter = 'all' | 'auth' | 'user' | 'connector' | 'settings' | 'routine'
const AUDIT_FILTERS: AuditFilter[] = ['all', 'auth', 'user', 'connector', 'settings', 'routine']

function auditBucket(action: string): Exclude<AuditFilter, 'all'> | null {
  if (/^(auth|login|logout)/.test(action)) return 'auth'
  if (action.startsWith('user')) return 'user'
  if (action.startsWith('connector')) return 'connector'
  if (action.startsWith('settings')) return 'settings'
  if (action.startsWith('routine')) return 'routine'
  return null
}

function auditRail(a: AuditEntry): Rail | undefined {
  return a.action.includes('failed') ? 'attention' : undefined
}

function AuditDetailModal({ entry, onClose }: { entry: AuditEntry; onClose(): void }) {
  const { t, i18n } = useTranslation()
  const rows: [string, ReactNode][] = [
    [t('admin.audit.time'), <span className="font-mono text-xs">{formatDateTime(entry.created_at, i18n.language)}</span>],
    [t('admin.audit.action'), <span className="font-mono text-xs">{entry.action}</span>],
    [t('admin.audit.user'), entry.user_email ?? entry.user_id ?? t('common.none')],
    [t('admin.audit.target'), entry.target ?? t('common.none')],
    [t('admin.audit.ip'), <span className="font-mono text-xs">{entry.actor_ip ?? t('common.none')}</span>]
  ]
  return (
    <Modal
      open title={t('admin.audit.details')} description={entry.action} onClose={onClose}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <dl className="grid grid-cols-[130px_1fr] gap-x-5 gap-y-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-ink-500">{label}</dt>
            <dd className="min-w-0 break-words">{value}</dd>
          </div>
        ))}
      </dl>
      {entry.meta && (
        <>
          <SectionLabel label={t('admin.audit.details')} />
          <pre className="mt-3 max-h-64 overflow-auto rounded-xl bg-cream-100 p-3 font-mono text-xs">{entry.meta}</pre>
        </>
      )}
    </Modal>
  )
}

export function AuditLogCard() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [items, setItems] = useState<AuditEntry[]>([])
  const [more, setMore] = useState(true)
  const [open, setOpen] = useState<string | null>(null)
  const load = async (before?: number) => {
    try {
      const r = await api.get<{ items: AuditEntry[] }>(`/api/admin/audit?limit=100${before ? `&before=${before}` : ''}`)
      setItems(cur => before ? [...cur, ...r.items] : r.items)
      setMore(r.items.length === 100)
    } catch (err) { toast((err as Error).message, 'error') }
  }
  useEffect(() => { void load() }, [])
  const table = useTable(items, {
    search: a => `${a.action} ${a.user_email ?? ''} ${a.target ?? ''}`,
    filter: (a, f) => f === 'all' || auditBucket(a.action) === f,
    perPage: 50
  })
  const detail = items.find(a => a.id === open) ?? null

  const exportCsv = () => {
    downloadCsv('audit-log.csv',
      [t('admin.audit.time'), t('admin.audit.action'), t('admin.audit.user'), t('admin.audit.target'), t('admin.audit.ip')],
      items.map(a => [new Date(a.created_at).toISOString(), a.action, a.user_email ?? a.user_id, a.target, a.actor_ip]))
    toast(t('admin.audit.exported'), 'success')
  }

  return (
    <>
      <PanelHead
        eyebrow={`${t('admin.title')} / ${t('settings.nav.audit')}`}
        title={t('admin.audit.title')}
        lede={<>{t('admin.audit.hint')} <AdminNote /></>}
        status={<StatusLine items={[<>{t('admin.audit.count', { count: items.length })}</>]} />}
        action={<Button variant="secondary" onClick={exportCsv}>{t('common.export')}</Button>}
      />

      <Toolbar>
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('admin.audit.searchPlaceholder')} />
        <FilterChips
          value={table.filter as AuditFilter} onChange={v => table.setFilter(v)} label={t('admin.audit.filterLabel')}
          options={AUDIT_FILTERS.map(f => ({ value: f, label: f === 'all' ? t('common.all') : t(`admin.audit.filter_${f}`) }))}
        />
        <Count>{t('admin.audit.count', { count: table.total })}</Count>
      </Toolbar>

      {items.length === 0 ? <EmptyState title={t('admin.audit.empty')} /> : (
        <Table
          footer={<>
            <span>{t('common.showing', { count: table.total, total: items.length })}</span>
            <span className="flex-1" />
            {more && <Button variant="ghost" size="sm" onClick={() => void load(items[items.length - 1].created_at)}>{t('admin.audit.older')}</Button>}
            <Pager page={table.page} pages={table.pages} onChange={table.setPage} />
          </>}
        >
          <thead>
            <tr>
              <Th rail />
              <Th>{t('admin.audit.time')}</Th>
              <Th>{t('admin.audit.action')}</Th>
              <Th>{t('admin.audit.user')}</Th>
              <Th>{t('admin.audit.target')}</Th>
              <Th>{t('admin.audit.ip')}</Th>
              <Th><span className="sr-only">{t('common.details')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(a => (
              <Row key={a.id} rail={auditRail(a)} onClick={() => setOpen(a.id)}>
                <Td><span className="font-mono text-xs text-ink-500">{formatDateTime(a.created_at, i18n.language)}</span></Td>
                <Td><span className="font-mono text-xs">{a.action}</span></Td>
                <Td className="text-xs">{a.user_email ?? a.user_id ?? t('common.none')}</Td>
                <Td className="max-w-[16rem] truncate text-xs text-ink-500">{a.target ?? ''}</Td>
                <Td><span className="font-mono text-xs text-ink-500">{a.actor_ip ?? ''}</span></Td>
                <Td><RowMenu items={[{ label: t('admin.audit.details'), onSelect: () => setOpen(a.id) }]} /></Td>
              </Row>
            ))}
          </tbody>
        </Table>
      )}

      {detail && <AuditDetailModal entry={detail} onClose={() => setOpen(null)} />}
    </>
  )
}

/* ================================================================ stats ==== */

/**
 * `/api/admin/stats` also returns what is running (version, commit, tag, build time — written by
 * `scripts/build-info.mjs`), plus the node version, the sandbox mode and the database size.
 */
type AdminStatsExtended = AdminStats & {
  version: string; commit: string; tag: string | null; dirty: boolean; builtAt: string | null
  node: string; sandbox: { mode: string; access: string }; dbBytes: number
}

/** Seconds as "2d 4h 7m". */
function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const parts: string[] = []
  if (d) parts.push(`${d}d`)
  if (d || h) parts.push(`${h}h`)
  parts.push(`${m}m`)
  return parts.join(' ')
}

export function Stats() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [data, setData] = useState<AdminStatsExtended | null>(null)
  const [at, setAt] = useState<number>(() => Date.now())
  const load = useCallback(() => {
    api.get<AdminStatsExtended>('/api/admin/stats')
      .then(d => { setData(d); setAt(Date.now()) })
      .catch(err => toast((err as Error).message, 'error'))
  }, [toast])
  useEffect(() => {
    void load()
    const id = setInterval(() => { void load() }, 5000)
    return () => clearInterval(id)
  }, [load])
  if (!data) return null

  const sandboxOn = data.sandbox.mode === 'docker'
  return (
    <>
      <PanelHead
        eyebrow={`${t('admin.title')} / ${t('settings.nav.status')}`}
        title={t('settings.nav.status')}
        lede={<>{t('admin.stats.hint')} <AdminNote /></>}
        status={<StatusLine items={[
          <span className="font-semibold text-mint-500">{t(data.runningTurns > 0 ? 'admin.stats.healthBusy' : 'admin.stats.healthOk')}</span>,
          <>{t('admin.stats.updated', { time: new Date(at).toLocaleTimeString(i18n.language) })}</>
        ]} />}
        action={<Button variant="secondary" onClick={() => void load()}>{t('admin.stats.refresh')}</Button>}
      />

      <Tiles>
        <Tile label={t('admin.stats.users')} value={data.users} />
        <Tile label={t('admin.stats.runtimes')} value={data.runtimes} rail="ok" />
        <Tile label={t('admin.stats.connections')} value={data.connections} />
        <Tile label={t('admin.stats.running')} value={data.runningTurns} rail={data.runningTurns > 0 ? 'attention' : undefined} />
        <Tile label={t('admin.stats.memory')} value={formatBytes(data.memory.rss)} note={`heap ${formatBytes(data.memory.heapUsed)}`} />
        <Tile label={t('admin.stats.uptime')} value={formatUptime(data.uptimeSec)} />
      </Tiles>

      <div className="mt-5">
        <Card title={t('admin.stats.server')}>
          <dl className="grid grid-cols-[150px_1fr] gap-x-5 gap-y-3 text-sm">
            {/* What is deployed: compare with the tag it should be on (docs/deployment.md §4). */}
            <dt className="text-ink-500">{t('admin.stats.version')}</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs font-semibold">{data.version}</span>
              {data.tag && <Badge tone="accent">{data.tag}</Badge>}
              {data.dirty && <Badge tone="danger">{t('admin.stats.dirty')}</Badge>}
            </dd>
            <dt className="text-ink-500">{t('admin.stats.commit')}</dt>
            <dd className="font-mono text-xs">
              {data.commit}
              {data.builtAt && <span className="ml-2 font-sans text-ink-500">{t('admin.stats.builtAt', { date: formatDateTime(Date.parse(data.builtAt), i18n.language) })}</span>}
            </dd>
            <dt className="text-ink-500">{t('admin.stats.node')}</dt>
            <dd className="font-mono text-xs">{data.node}</dd>
            <dt className="text-ink-500">{t('admin.stats.db')}</dt>
            <dd className="tabular-nums">{formatBytes(data.dbBytes)}</dd>
            <dt className="text-ink-500">{t('admin.stats.sandbox')}</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <Badge tone={sandboxOn ? 'success' : 'neutral'}>{t(sandboxOn ? 'admin.stats.sandboxOn' : 'admin.stats.sandboxOff')}</Badge>
              <span className="text-ink-400">{data.sandbox.access}</span>
            </dd>
            <dt className="text-ink-500">{t('admin.stats.uptime')}</dt>
            <dd>{formatUptime(data.uptimeSec)}</dd>
          </dl>
        </Card>
      </div>
    </>
  )
}
